import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { usePageTitle } from '../hooks/usePageTitle'
import { MagicLinkSignIn } from '../components/MagicLinkSignIn'
import {
  ClubButton,
  ClubLinkButton,
  Crest,
  ExpiredLinkNotice,
  JoinCard,
  JoinPage,
} from '../components/golf'

/**
 * The members' entrance (route map §4 `/sign-in`).
 *
 * Every "Sign in" in the club used to land on /join, which is headed "Apply for
 * membership" and pitches founding seats — so a member coming back was read a
 * sales page and left to work out that typing their address would let them in.
 * This is the same card in the club's own sign-in voice; /join stays the
 * application.
 *
 * JoinPage chrome rather than ClubPage: its rationale — "once someone is
 * applying, the marketing nav is just a set of ways to abandon the form" —
 * holds harder for a member with exactly one task. ClubPage would also render a
 * ClubHeader whose "Sign in" link points at this very page. Brandlock still
 * links home, so the way out survives.
 */
export function SignInScreen() {
  usePageTitle('Sign in')
  const { isAuthenticated, signOut } = useAuth()
  const [signingOut, setSigningOut] = useState(false)

  const handleSignOut = async () => {
    setSigningOut(true)
    await signOut()
    setSigningOut(false)
  }

  // Only a typed URL or a stale bookmark reaches this signed in — every inbound
  // link renders in a signed-out branch. Not a redirect: "you are already in"
  // plus the door out is the honest answer on a shared machine, where a silent
  // bounce to /clubhouse would strand someone trying to sign in as themselves.
  if (isAuthenticated) {
    return (
      <JoinPage stepLabel="The Members' Entrance">
        <JoinCard>
          <div className="flex flex-1 flex-col text-center">
            <Crest monogram className="mx-auto mb-5 size-[66px]" />
            <h1 className="font-display text-3xl font-medium tracking-[-0.01em] text-pine">
              You&rsquo;re already through the gate.
            </h1>
            <p className="mx-auto mb-6.5 mt-3 max-w-[32ch] text-[15px] text-ink-soft">
              Your card is in order. The clubhouse is open.
            </p>
            {/*
              Sign out HERE, rather than linking to the account page where the
              control lives. A member with no settled standing cannot reach
              /clubhouse/account at all — ClubhouseLayout sends every clubhouse
              path to /join/checkout — so a link there is a dead end for exactly
              the person most likely to be standing on this page wanting out.
              Calling signOut() directly is also simply the shorter answer to
              "not you?" on a shared machine.
            */}
            <div className="mt-auto">
              <ClubLinkButton to="/clubhouse" className="w-full">
                To the clubhouse
              </ClubLinkButton>
              <ClubButton
                type="button"
                variant="ghost"
                className="mt-3 w-full"
                disabled={signingOut}
                onClick={handleSignOut}
              >
                {signingOut ? 'Signing out…' : 'Not you? Sign out'}
              </ClubButton>
            </div>
          </div>
        </JoinCard>
      </JoinPage>
    )
  }

  return (
    <JoinPage stepLabel="The Members' Entrance">
      <ExpiredLinkNotice />

      <JoinCard>
        <MagicLinkSignIn mode="signin" />
      </JoinCard>

      {/*
       * Below the card, not inside it: MagicLinkSignIn is flex-1 with mt-auto on
       * its footer, so anything threaded in fights that layout in both states —
       * and would have to decide what to do in the sent state. Navigation
       * belongs to the page, exactly as the notice above does.
       *
       * To /membership rather than /join: it matches the header's Apply CTA and
       * gives the click real content — tiers, terms — instead of the same form
       * under a different heading.
       */}
      <p className="mt-7 text-center text-[13.5px] text-ink-soft">
        Not a member yet?{' '}
        <Link to="/membership" className="font-semibold text-fairway">
          Apply &rarr;
        </Link>
      </p>
    </JoinPage>
  )
}
